import { useState, useEffect } from 'react';
import { Sparkles, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export const NewsletterComposer = ({ candidates, onCreate }) => {
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState('Nouveautés');
  const [subtitle, setSubtitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelected(new Set(candidates.slice(0, 6).map(p => p.id)));
  }, [candidates]);

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const submit = async () => {
    if (selected.size === 0) { toast.error('Sélectionnez au moins un produit'); return; }
    setLoading(true);
    try {
      await onCreate({ title, subtitle: subtitle || null, message: message || null,
        product_ids: candidates.filter(p => selected.has(p.id)).map(p => p.id) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mb-10" data-testid="newsletter-composer">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-white text-lg mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[color:var(--color-primary)]" /> Nouveautés & retours en stock (30 derniers jours)
            <span className="text-zinc-500 text-sm">— {selected.size} sélectionné(s)</span>
          </h2>
          {candidates.length === 0 ? (
            <p className="text-zinc-500 text-sm border border-white/10 p-6 text-center" data-testid="newsletter-no-candidates">
              Aucune nouveauté sur les 30 derniers jours.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="newsletter-candidates">
              {candidates.map(p => {
                const on = selected.has(p.id);
                return (
                  <button type="button" key={p.id} onClick={() => toggle(p.id)}
                    className={`text-left border p-2 transition-colors ${on ? 'border-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]' : 'border-white/10 bg-[color:var(--color-surface)] hover:border-white/30'}`}
                    data-testid={`newsletter-candidate-${p.id}`}>
                    <div className="aspect-[4/5] bg-black overflow-hidden mb-2 relative">
                      {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
                      <span className={`absolute top-1 left-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${p.badge === 'new' ? 'bg-[color:var(--color-primary)] text-black' : 'bg-emerald-500 text-black'}`}>
                        {p.badge === 'new' ? 'Nouveau' : 'Retour'}
                      </span>
                    </div>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest truncate">{p.brand}</p>
                    <p className="text-white text-sm truncate">{p.name}</p>
                    <p className="text-zinc-400 text-xs">{p.price_detail}€ · <span className="text-[color:var(--color-primary)]">{p.price_gros}€</span></p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-[color:var(--color-surface)] p-5 space-y-4 h-fit">
          <h3 className="text-white flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[color:var(--color-primary)]" /> Affiche & message</h3>
          <div>
            <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Titre</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-3 py-2 outline-none" data-testid="newsletter-title" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Sous-titre (optionnel)</label>
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Arrivage du jour"
              className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-3 py-2 outline-none" data-testid="newsletter-subtitle" />
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase tracking-widest mb-1 block">Message WhatsApp (optionnel)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
              placeholder="Laissez vide pour un message généré automatiquement avec la liste des produits et le lien du catalogue."
              className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-3 py-2 outline-none text-sm resize-none" data-testid="newsletter-message" />
          </div>
          <p className="text-zinc-500 text-xs">6 produits max par affiche story, 4 par affiche carrée — au-delà, plusieurs pages sont générées.</p>
          <button type="button" onClick={submit} disabled={loading}
            className="w-full bg-[color:var(--color-primary)] text-black py-3 hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50 text-sm uppercase tracking-widest"
            data-testid="newsletter-create-btn">
            {loading ? 'Génération...' : "Générer l'affiche"}
          </button>
        </div>
      </div>
    </section>
  );
};

export const CampaignHistory = ({ campaigns, onOpen }) => (
  <section data-testid="newsletter-history">
    <h2 className="text-white text-lg mb-3">Campagnes précédentes</h2>
    {campaigns.length === 0 ? (
      <p className="text-zinc-500 text-sm">Aucune campagne pour le moment.</p>
    ) : (
      <ul className="divide-y divide-white/5 border border-white/10">
        {campaigns.map(c => (
          <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5">
            <div>
              <p className="text-white text-sm">{c.title} <span className="text-zinc-500">— {c.subtitle}</span></p>
              <p className="text-zinc-500 text-xs">
                {new Date(c.created_at).toLocaleDateString('fr-FR')} · {c.product_ids.length} produit(s) · WhatsApp {c.whatsapp_sent?.length || 0} envoyé(s)
                {c.email_status?.state === 'done' && ` · e-mail ${c.email_status.sent}/${c.email_status.total}`}
              </p>
            </div>
            <button type="button" onClick={() => onOpen(c.id)} className="text-[color:var(--color-primary)] text-sm hover:underline"
              data-testid={`campaign-open-${c.id}`}>Ouvrir</button>
          </li>
        ))}
      </ul>
    )}
  </section>
);
